export { AmplitudeCore } from './core-client';
export { CoreClient, PluginHost } from './types/client/core-client';
export { AnalyticsClient } from './types/client/analytics-client';
export { AmplitudeContext } from './types/amplitude-context';
export { Identify, IIdentify, ValidPropertyType } from './identify';
export { Revenue, IRevenue, RevenueProperty } from './revenue';
export { Destination } from './plugins/destination';
export { IdentityEventSender } from './plugins/identity';
export { Config, RequestMetadata } from './config';
export { IConfig } from './types/config/core-config';
export { Logger, ILogger, LogConfig } from './logger';
export { getGlobalScope } from './global-scope';
export { getAnalyticsConnector, setConnectorDeviceId, setConnectorUserId } from './analytics-connector';
export { isNewSession } from './session';
export { getCookieName, getOldCookieName } from './cookie-name';
export { getLanguage } from './language';
export { getQueryParams } from './query-params';

export { returnWrapper, AmplitudeReturn } from './utils/return-wrapper';
export { debugWrapper, getClientLogConfig, getClientStates } from './utils/debug';
export { UUID } from './utils/uuid';
export { createIdentifyEvent } from './utils/event-builder';
export { isUrlMatchAllowlist, getDecodeURI } from './utils/url-utils';
export { generateHashCode, isTimestampInSample } from './utils/sampling';

export { MemoryStorage } from './storage/memory';
export { CookieStorage, isDomainEqual, decodeCookieValue } from './storage/cookie';
export { getStorageKey } from './storage/helpers';

export { BrowserStorage } from './storage/browser-storage';

export { DiagnosticsClient, IDiagnosticsClient } from './diagnostics/diagnostics-client';
export { registerSdkLoaderMetadata } from './diagnostics/uncaught-sdk-errors';

export { BaseTransport } from './transports/base';
export { FetchTransport } from './transports/fetch';
export {
  compressToGzipArrayBuffer,
  isCompressionStreamAvailable,
  MIN_GZIP_UPLOAD_BODY_SIZE_BYTES,
} from './transports/gzip';

export { RemoteConfigClient, IRemoteConfigClient, RemoteConfig, Source } from './remote-config/remote-config';

export { LogLevel } from './types/loglevel';
export { AMPLITUDE_PREFIX, STORAGE_PREFIX } from './types/constants';
export { Storage, IdentityStorageType, CookieStorageConfig } from './types/storage';
export { consoleObserver } from './observers/console';
export {
  Event,
  IdentifyOperation,
  SpecialEventType,
  IdentifyEvent,
  GroupIdentifyEvent,
  IdentifyUserProperties,
} from './types/event/event';
export { EventOptions, BaseEvent } from './types/event/base-event';
export { IngestionMetadata } from './types/event/ingestion-metadata';
export { ServerZoneType, ServerZone } from './types/server-zone';
export { OfflineDisabled } from './types/offline';
export { Plan } from './types/event/plan';
export { TransportType, Transport, TransportOptions, TransportTypeOrOptions } from './types/transport';
export { Payload } from './types/payload';
export { Response } from './types/response';
export { UserSession } from './types/user-session';
export {
  Plugin,
  BeforePlugin,
  DestinationPlugin,
  EnrichmentPlugin,
  PluginType,
  AnalyticsIdentity,
} from './types/plugin';
export { Result } from './types/result';
export {
  ElementInteractionsOptions,
  Messenger,
  ActionType,
  DEFAULT_CSS_SELECTOR_ALLOWLIST,
  DEFAULT_DATA_ATTRIBUTE_PREFIX,
  DEFAULT_ACTION_CLICK_ALLOWLIST,
  LabeledEvent,
  Trigger,
  DataSource,
} from './types/element-interactions';

export {
  FrustrationInteractionsOptions,
  DEFAULT_DEAD_CLICK_ALLOWLIST,
  DEFAULT_RAGE_CLICK_ALLOWLIST,
  DEFAULT_ERROR_CLICK_ALLOWLIST,
  DEFAULT_RAGE_CLICK_THRESHOLD,
  DEFAULT_RAGE_CLICK_WINDOW_MS,
  DEFAULT_RAGE_CLICK_OUT_OF_BOUNDS_THRESHOLD,
  DEFAULT_DEAD_CLICK_WINDOW_MS,
} from './types/frustration-interactions';
export { PageTrackingOptions, PageTrackingTrackOn, PageTrackingHistoryChanges } from './types/page-view-tracking';
export { FormInteractionsOptions } from './types/form-interactions';
export { Status } from './types/status';

export { NetworkEventCallback, networkObserver } from './observers/network';
export { NetworkRequestEvent, IRequestWrapper, JsonObject, JsonValue, JsonArray } from './network-request-event';
export { NetworkTrackingOptions, NetworkCaptureRule } from './types/network-tracking';
export { SAFE_HEADERS, FORBIDDEN_HEADERS } from './types/constants';

export { PageUrlEnrichmentOptions } from './types/page-url-enrichment';

// Campaign
export { Campaign, UTMParameters, ReferrerParameters, ClickIdParameters, ICampaignParser } from './types/campaign';
export { EMPTY_VALUE, BASE_CAMPAIGN, MKTG } from './types/constants';
export { CampaignParser } from './campaign/campaign-parser';
export {
  getPageTitle,
  TEXT_MASK_ATTRIBUTE,
  MASKED_TEXT_VALUE,
  replaceSensitiveString,
  CC_REGEX,
  SSN_REGEX,
  EMAIL_REGEX,
} from './plugins/helpers';

// Browser
export {
  BrowserConfig,
  BrowserOptions,
  DefaultTrackingOptions,
  TrackingOptions,
  AutocaptureOptions,
  CookieOptions,
  AttributionOptions,
  RemoteConfigOptions,
} from './types/config/browser-config';
export { BrowserClient } from './types/client/browser-client';

// Node
export { NodeClient } from './types/client/node-client';
export { NodeConfig, NodeOptions } from './types/config/node-config';

// React Native
export {
  ReactNativeConfig,
  ReactNativeTrackingOptions,
  ReactNativeOptions,
  ReactNativeAttributionOptions,
} from './types/config/react-native-config';
export { ReactNativeClient } from './types/client/react-native-client';

export { Observable, asyncMap, merge, multicast, Unsubscribable } from './utils/observable';

export { InstanceProxy } from './types/proxy';
export { safeJsonStringify } from './utils/safe-stringify';

// Messenger (cross-window communication)
export type { BaseWindowMessenger, ActionHandler } from './messenger/base-window-messenger';
export { getOrCreateWindowMessenger } from './messenger/base-window-messenger';
export { enableBackgroundCapture } from './messenger/background-capture';
export {
  AMPLITUDE_ORIGIN,
  AMPLITUDE_ORIGIN_EU,
  AMPLITUDE_ORIGIN_STAGING,
  AMPLITUDE_ORIGINS_MAP,
  AMPLITUDE_BACKGROUND_CAPTURE_SCRIPT_URL,
} from './messenger/constants';

export { ExcludeInternalReferrersOptions, EXCLUDE_INTERNAL_REFERRERS_CONDITIONS } from './types/config/browser-config';
